import { useEffect, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { safeRefreshTokens } from '@config/axios/axios.config.ts';
import { useAuthTokens } from '@store';

export type NotificationsEventType = 'notification.created';

export interface NotificationEventType {
  type: NotificationsEventType;
  payload: {
    recipient_id: string;
    notification: {
      id: string;
      source: 'task';
      status: 'unread' | 'read';
      author_name: string | null;
      author_id: string | null;
      description: string;
      task_id: string | null;
      patient_id: string | null;
      created_at: string;
    };
  };
}

export const useNotificationsEvents = () => {
  const { token } = useAuthTokens();
  const [event, setEvent] = useState<NotificationEventType | null>(null);

  useEffect(() => {
    if (!token) {
      return;
    }

    const controller = new AbortController();
    let isCancelled = false;
    let retryTimeout: number | undefined;
    let retryDelay = 1000;

    const subscribe = async () => {
      try {
        await fetchEventSource(
          `${import.meta.env.VITE_BASE_URL}/notifications/sse`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
            async onopen(response) {
              if (response.ok) {
                return;
              }

              if (response.status === 401) {
                controller.abort();
                await safeRefreshTokens();
                throw new Error('unauthorized');
              }

              throw new Error(`Unexpected status ${response.status}`);
            },
            onmessage(ev) {
              if (!ev.data) {
                return;
              }

              try {
                const parsed = JSON.parse(ev.data);
                setEvent(parsed);
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log('Downloads SSE error', err);
              }
            },
            onerror(err) {
              // eslint-disable-next-line no-console
              console.log('Downloads SSE error', err);
              throw err;
            },
            openWhenHidden: true,
          },
        );
        retryDelay = 1000;
      } catch (err) {
        if (isCancelled) {
          return;
        }

        const message = (err as Error).message;

        if (message === 'unauthorized') {
          return;
        }

        retryTimeout = window.setTimeout(() => {
          if (!isCancelled) {
            subscribe();
          }
        }, retryDelay);

        retryDelay = Math.min(retryDelay * 2, 30000);
      }
    };

    void subscribe();

    return () => {
      isCancelled = true;
      controller.abort();
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [token]);

  return {
    event,
  };
};
